import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable, timer } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";

import { Balance, LoginResponse, UserDetails, UserState } from "./user.model";
import { CoreService } from "../core.service";
import { sessionStorageItems } from "../core.models";

@Injectable({
  providedIn: "root",
})
export class UserStore extends ComponentStore<UserState> {
  private readonly platformBaseURL = this.coreService.platformBaseUrl;

  constructor(private http: HttpClient, private coreService: CoreService) {
    super({});

    // Caching of user details specifically in case a interaction is active.
    const sid = this.coreService.getSessionIdFromCookie();
    if (this.coreService.hasCachedUserDetails() && !sid) {
      this.coreService.clearUserDetails();
    } else if (!this.coreService.hasCachedUserDetails() && sid) {
      this.getUserDetails(sid);
      this.userDetails$.pipe(
        filter((x): x is UserDetails => !!x)
      ).subscribe(userDetails => {
        sessionStorage.setItem(sessionStorageItems.UserDetails, JSON.stringify(userDetails));
      })
    }
  }

  readonly userDetails$: Observable<UserDetails> = this.select(
    (state) => state.userDetails
  );
  readonly userBalance$: Observable<Balance> = this.select(
    (state) => state.userBalance
  );

  readonly addUserDetails = this.updater((state, userDetails: UserDetails) => ({
    ...state,
    userDetails: { ...userDetails },
  }));

  readonly addUserBalance = this.updater((state, userBalance: Balance) => ({
    ...state,
    userBalance: { ...userBalance },
  }));

  public login(formData: FormData): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(this.coreService.legacyUrl + `/ajax/autheticate_user.php`, formData)
      .pipe(tap((x) => {
        sessionStorage.setItem(sessionStorageItems.UserDetails, JSON.stringify(x.userDetails));
        this.addUserDetails(x.userDetails);
      }));
  }

  readonly getUserDetails = this.effect((sessionId$: Observable<string>) => {
    return sessionId$.pipe(
      switchMap(id => {
        return this.http.post<UserDetails>(`${this.platformBaseURL}getuserdetails`, {
          sessionid: id
        }).pipe(tap(userDetails => this.addUserDetails(userDetails)))
      })
    )
  });

  readonly startBalancePolling = this.effect((sessionId$: Observable<string>) => {
    return sessionId$.pipe(
      switchMap((id) =>
        timer(0, 5000) // a bit concerned about performance here as the header is available on every page
          .pipe(
            switchMap(() =>
              this.http.post<Balance>(`${this.platformBaseURL}getbalance`, {
                sessionid: id,
                touchSession: false,
              })
            )
          )
          .pipe(tap((balance: Balance) => {
            if(balance && balance.hasOwnProperty('code')) {
              if(balance.code === 254) {
                location.reload();
              }
            }
            this.addUserBalance(balance)
          }))
      )
    );
  });
}
